import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  FileCheck,
  FileX,
  Clock,
  ExternalLink
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIPLOMAS = [
  "BPJEPS Activités de la Forme",
  "DEJEPS Perfectionnement Sportif",
  "CQP Instructeur Fitness",
  "Licence STAPS",
  "Master STAPS",
  "Autre diplôme sportif"
];

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  phone: "",
  specialty: "",
  diploma: "",
  bio: "",
  photo_url: "",
  has_training_access: true,
  is_active: true
};

const CoachAccountsManagement = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [docsCoach, setDocsCoach] = useState(null);

  const fetchCoaches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/coach-accounts/`, { withCredentials: true });
      setCoaches(response.data);
      // Refresh the docs dialog if open
      setDocsCoach((current) => current ? response.data.find((c) => c.id === current.id) || null : null);
    } catch (err) {
      console.error("Failed to fetch coaches:", err);
      toast.error("Erreur lors du chargement des coachs");
    } finally {
      setLoading(false);
    }
  }, []);

  const previewDocument = async (coachId, docType) => {
    try {
      const response = await axios.get(`${API}/coach-accounts/${coachId}/documents/${docType}/file`, {
        withCredentials: true,
        responseType: "blob"
      });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, "_blank");
    } catch (err) {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  const setDocumentStatus = async (coachId, docType, status) => {
    try {
      await axios.put(`${API}/coach-accounts/${coachId}/documents/${docType}/status`, { status }, { withCredentials: true });
      toast.success(status === "validated" ? "Document validé" : "Document refusé");
      fetchCoaches();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCoach) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API}/coach-accounts/${editingCoach.id}`, updateData, { withCredentials: true });
        toast.success("Coach mis à jour avec succès");
      } else {
        await axios.post(`${API}/coach-accounts/`, formData, { withCredentials: true });
        toast.success("Compte coach créé avec succès");
      }

      setDialogOpen(false);
      resetForm();
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (coach) => {
    setEditingCoach(coach);
    setFormData({
      name: coach.name,
      email: coach.email,
      password: "",
      phone: coach.phone,
      specialty: coach.specialty,
      diploma: coach.diploma,
      bio: coach.bio,
      photo_url: coach.photo_url || "",
      has_training_access: coach.has_training_access,
      is_active: coach.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce compte coach ?")) return;

    try {
      await axios.delete(`${API}/coach-accounts/${id}`, { withCredentials: true });
      toast.success("Compte coach supprimé");
      fetchCoaches();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleActive = async (coach) => {
    try {
      await axios.put(`${API}/coach-accounts/${coach.id}`, { is_active: !coach.is_active }, { withCredentials: true });
      toast.success(`Coach ${coach.is_active ? "désactivé" : "activé"}`);
      fetchCoaches();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const toggleTrainingAccess = async (coach) => {
    try {
      await axios.put(`${API}/coach-accounts/${coach.id}`, { has_training_access: !coach.has_training_access }, { withCredentials: true });
      toast.success(`Accès formation ${coach.has_training_access ? "désactivé" : "activé"}`);
      fetchCoaches();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const resetForm = () => {
    setEditingCoach(null);
    setFormData(EMPTY_FORM);
  };

  return (
    <div data-testid="coach-accounts-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Outfit'] text-2xl font-bold uppercase">
          Comptes Coachs
        </h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <button
              className="btn-primary flex items-center gap-2 py-3 px-6"
              data-testid="add-coach-account-button"
            >
              <Plus className="w-5 h-5" />
              Créer un compte
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F16] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-xl uppercase">
                {editingCoach ? "Modifier le compte" : "Créer un compte coach"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="coach-account-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Nom complet *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-[#05050A] border-white/10"
                    required
                  />
                </div>
                <div>
                  <Label className="form-label">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#05050A] border-white/10"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">
                    Mot de passe {editingCoach ? "(laisser vide pour ne pas changer)" : "*"}
                  </Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-[#05050A] border-white/10"
                    required={!editingCoach}
                  />
                </div>
                <div>
                  <Label className="form-label">Téléphone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-[#05050A] border-white/10"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Spécialité *</Label>
                  <Input
                    value={formData.specialty}
                    onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                    className="bg-[#05050A] border-white/10"
                    placeholder="Musculation, HIIT, etc."
                    required
                  />
                </div>
                <div>
                  <Label className="form-label">Diplôme *</Label>
                  <Select
                    value={formData.diploma}
                    onValueChange={(value) => setFormData({ ...formData, diploma: value })}
                  >
                    <SelectTrigger className="bg-[#05050A] border-white/10">
                      <SelectValue placeholder="Sélectionnez un diplôme" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F16] border-white/10">
                      {DIPLOMAS.map((d) => (
                        <SelectItem key={d} value={d} className="hover:bg-[#0066FF]/20">
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="form-label">Bio *</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="bg-[#05050A] border-white/10"
                  rows={3}
                  required
                />
              </div>
              <div>
                <Label className="form-label">URL de la photo</Label>
                <Input
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  className="bg-[#05050A] border-white/10"
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_active">Compte actif</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="has_training_access"
                    checked={formData.has_training_access}
                    onChange={(e) => setFormData({ ...formData, has_training_access: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="has_training_access">Accès à la formation</Label>
                </div>
              </div>
              <button
                type="submit"
                className="w-full btn-primary"
              >
                {editingCoach ? "Mettre à jour" : "Créer le compte"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : coaches.length === 0 ? (
        <div className="text-center py-12 text-[#94A3B8]">
          Aucun compte coach créé
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="coach-accounts-table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Contact</th>
                <th>Spécialité</th>
                <th>Documents</th>
                <th>Formation</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coaches.map((coach) => (
                <tr key={coach.id} data-testid={`coach-account-row-${coach.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img
                        src={coach.photo_url || "https://images.unsplash.com/photo-1701481063068-0103e348d844?w=100"}
                        alt={coach.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold">{coach.name}</div>
                        {coach.rcpro_number && (
                          <div className="text-xs text-[#64748B]">RC Pro: {coach.rcpro_number}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="text-sm">
                      <div>{coach.email}</div>
                      <div className="text-[#94A3B8]">{coach.phone}</div>
                    </div>
                  </td>
                  <td>{coach.specialty}</td>
                  <td>
                    <button
                      onClick={() => setDocsCoach(coach)}
                      className={`status-badge cursor-pointer ${coach.documents_validated ? 'status-accepted' : 'status-pending'}`}
                      data-testid={`view-docs-${coach.id}`}
                    >
                      <FileText className="w-3 h-3" />
                      <span className="ml-1">{coach.documents_validated ? 'Validés' : 'À vérifier'}</span>
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleTrainingAccess(coach)}
                      className={`status-badge cursor-pointer ${coach.has_training_access ? 'status-accepted' : 'status-rejected'}`}
                    >
                      {coach.has_training_access ? 'Activé' : 'Désactivé'}
                    </button>
                  </td>
                  <td>
                    <span className={`status-badge ${coach.is_active ? 'status-accepted' : 'status-rejected'}`}>
                      {coach.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleActive(coach)}
                        className="p-2 hover:bg-white/10 rounded transition-colors"
                        title={coach.is_active ? "Désactiver" : "Activer"}
                      >
                        {coach.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleEdit(coach)}
                        className="p-2 hover:bg-white/10 rounded transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(coach.id)}
                        className="p-2 hover:bg-red-500/20 rounded transition-colors text-red-500"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Documents Review Dialog */}
      <Dialog open={!!docsCoach} onOpenChange={(open) => { if (!open) setDocsCoach(null); }}>
        <DialogContent className="bg-[#0F0F16] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl uppercase">
              Documents de {docsCoach?.name}
            </DialogTitle>
          </DialogHeader>
          {docsCoach && (
            <div className="space-y-4 mt-4" data-testid="docs-review-dialog">
              <div className="bg-[#05050A] border border-white/10 p-3 text-sm">
                <span className="text-[#94A3B8]">RC Pro N°:</span>{" "}
                <span className="text-white font-semibold">{docsCoach.rcpro_number || "Non renseigné"}</span>
              </div>
              {[
                { key: "diploma", label: "Diplôme sportif" },
                { key: "carte_pro", label: "Carte professionnelle" },
                { key: "rcpro", label: "Attestation RC Pro" }
              ].map(({ key, label }) => {
                const doc = docsCoach.documents?.[key];
                return (
                  <div key={key} className="bg-[#05050A] border border-white/10 p-4" data-testid={`admin-doc-${key}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h4 className="font-semibold mb-1">{label}</h4>
                        {doc ? (
                          <p className="text-xs text-[#64748B]">{doc.original_filename} · {(doc.size / 1024).toFixed(0)} KB · {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}</p>
                        ) : (
                          <p className="text-xs text-[#64748B] italic">Document non déposé</p>
                        )}
                      </div>
                      {doc && (
                        <div className="flex items-center gap-2">
                          <span className={`status-badge text-xs ${doc.status === 'validated' ? 'status-accepted' : doc.status === 'rejected' ? 'status-rejected' : 'status-pending'}`}>
                            {doc.status === 'validated' && <FileCheck className="w-3 h-3 mr-1" />}
                            {doc.status === 'rejected' && <FileX className="w-3 h-3 mr-1" />}
                            {doc.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {doc.status === 'validated' ? 'Validé' : doc.status === 'rejected' ? 'Refusé' : 'En attente'}
                          </span>
                        </div>
                      )}
                    </div>
                    {doc && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <button
                          onClick={() => previewDocument(docsCoach.id, key)}
                          className="flex items-center gap-1 text-sm px-3 py-2 border border-white/10 hover:border-[#0066FF] hover:bg-[#0066FF]/10 transition-colors"
                          data-testid={`admin-preview-${key}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                          Aperçu
                        </button>
                        <button
                          onClick={() => setDocumentStatus(docsCoach.id, key, "validated")}
                          disabled={doc.status === 'validated'}
                          className="flex items-center gap-1 text-sm px-3 py-2 bg-green-500/10 text-green-500 border border-green-500/30 hover:bg-green-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`admin-validate-${key}`}
                        >
                          <FileCheck className="w-4 h-4" />
                          Valider
                        </button>
                        <button
                          onClick={() => setDocumentStatus(docsCoach.id, key, "rejected")}
                          disabled={doc.status === 'rejected'}
                          className="flex items-center gap-1 text-sm px-3 py-2 bg-red-500/10 text-red-500 border border-red-500/30 hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`admin-reject-${key}`}
                        >
                          <FileX className="w-4 h-4" />
                          Refuser
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoachAccountsManagement;
